import { selectActiveWorkspace } from 'lib/helpers/selectors'
import {
  ChangeEvent,
  MutableRefObject,
  useCallback,
  useRef,
  useState,
} from 'react'
import { useAppSelector } from 'store/hooks'
import { ICFDIsXMLUploadContainerProps } from './CFDIsXMLUploadContainer'
import useUploadXMLCFDIs from 'hooks/Invoices/CFDIs/useUploadXMLCFDIs'

interface IUseCFDIsXMLUploadContainer {
  filesReceived: File[]
  inputFile: MutableRefObject<HTMLInputElement | null>
  handleSelectFile: (event: ChangeEvent<HTMLInputElement>) => void
  handleRemoveFile: (objectKey: number) => () => void
  handleReplaceFileWindow: (idToReplace: number) => () => void
  handleUploadFiles: () => void
}

const useCFDIsXMLUploadContainer = ({
  files,
  handleCloseDrawer,
}: ICFDIsXMLUploadContainerProps): IUseCFDIsXMLUploadContainer => {
  const workspaceId = useAppSelector(selectActiveWorkspace)

  const [filesReceived, setFilesReceived] = useState<File[]>(files ? files : [])
  const [xmlIdToReplace, setXmlIdToReplace] = useState<number | null>(null)

  const inputFile = useRef<HTMLInputElement | null>(null)

  const { mutate: uploadXMLCFDIs } = useUploadXMLCFDIs(handleCloseDrawer)

  const handleSelectFile = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files: FileList | null = event.target.files
      if (files && files.length > 0 && xmlIdToReplace !== null) {
        const filesArray = Array.from(files)
        let newFilesArray = filesReceived.map((file, index) => {
          if (xmlIdToReplace === index) {
            return filesArray[0]
          } else {
            return file
          }
        })

        setFilesReceived(newFilesArray)
      }
    },
    [filesReceived, xmlIdToReplace]
  )

  const handleRemoveFile = useCallback(
    (objectKey?: number) => () => {
      if (filesReceived !== null && objectKey !== undefined) {
        const filesAfterRemoval = filesReceived.filter(
          (_, i) => objectKey !== i
        )
        setFilesReceived(filesAfterRemoval)
        filesAfterRemoval.length === 0 && handleCloseDrawer()
      }
    },
    [filesReceived, handleCloseDrawer]
  )

  const handleReplaceFileWindow = useCallback(
    (idToReplace: number) => () => {
      if (inputFile !== null && inputFile.current !== null) {
        inputFile.current.click()
        setXmlIdToReplace(idToReplace)
      }
    },
    [inputFile]
  )

  const handleUploadFiles = useCallback(() => {
    uploadXMLCFDIs({
      workspaceId: workspaceId,
      files: filesReceived,
    })
  }, [workspaceId, filesReceived, uploadXMLCFDIs])

  return {
    filesReceived,
    inputFile,
    handleSelectFile,
    handleRemoveFile,
    handleReplaceFileWindow,
    handleUploadFiles,
  }
}

export default useCFDIsXMLUploadContainer
